import type {
  CompanyRegistrationData,
  RegisterUserWithTokenData,
  User,
} from "@/hooks/useAuth";
import { axiosInstance } from "@/lib/axios";
import { useMutation } from "@tanstack/react-query";

interface LoginCredentials {
  email: string;
  password: string;
  isCompany?: boolean;
}

interface LoginResponse {
  status: string;
  token: string;
  data: {
    document: User;
  };
}

export const useLogin = () => {
  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const { data } = await axiosInstance.post<LoginResponse>(
        "/api/v1/authentication/user/login",
        credentials
      );
      return data;
    },
  });
};

export const useRegisterUser = () => {
  return useMutation({
    mutationFn: async ({
      userData,
      token,
    }: {
      userData: RegisterUserWithTokenData;
      token: string;
    }) => {
      const { data } = await axiosInstance.post(
        `/api/v1/authentication/user/signup?token=${token}`,
        userData
      );
      return data;
    },
  });
};

export const useRegisterCompany = () => {
  return useMutation({
    mutationFn: async (data: CompanyRegistrationData) => {
      const { data: responseData } = await axiosInstance.post(
        "/api/v1/authentication/company/signup",
        data
      );
      return responseData;
    },
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: async (userData: Partial<User> | FormData) => {
      const { data } = await axiosInstance.patch(
        "/api/v1/users/update-me",
        userData
      );
      return data;
    },
  });
};

export const useUpdateCompany = () => {
  return useMutation({
    mutationFn: async (companyData: FormData) => {
      const { data } = await axiosInstance.patch(
        "/api/v1/companies/update-me",
        companyData
      );
      return data;
    },
  });
};
